export interface PageSEOConfig {
  title: string;
  description: string;
  schema?: Record<string, any> | Array<Record<string, any>>;
}

export const SEO_DATA: Record<string, PageSEOConfig> = {
  home: {
    title: "25th & Staffing — Retained Executive Search, Rooted in Africa",
    description:
      "25th and Staffing is a global, human-led, AI-powered retained search and workforce solutions. Eleven years placing senior talent globally. Executive search, permanent staffing, and workforce technology.",
  },
  executiveSearch: {
    title: "Job Marketplace & Career Opportunities — 25th & Staffing",
    description:
      "Retained executive search across industries and leadership levels. Defined process, shortlist delivery timeline, and placement guarantee. Rooted in Africa, reaching anywhere.",
    schema: {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: "25th & Staffing Executive Search",
      provider: {
        "@type": "Organization",
        name: "25th & Staffing",
      },
      serviceType: "Retained Executive Search",
      description:
        "Retained executive search across industries, functions, and leadership levels. Targeted sourcing, structured assessment, and market intelligence. Defined process, shortlist delivery timeline, and placement guarantee.",
      areaServed: "Worldwide",
      offers: {
        "@type": "Offer",
        name: "Retained Executive Search",
        description:
          "Exclusive retained search mandate with search commencement deposit, shortlist delivery timeline, and placement guarantee.",
      },
    },
  },
  permanentStaffing: {
    title: "Permanent Staffing Solutions — 25th & Staffing",
    description:
      "End-to-end permanent staffing for critical hires and high-volume workforce needs. Search, screening, assessment, offer management, and post-placement support.",
    schema: {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: "25th & Staffing Permanent Staffing",
      provider: {
        "@type": "Organization",
        name: "25th & Staffing",
      },
      serviceType: "Permanent Staffing",
      description:
        "End-to-end permanent staffing solutions for critical hires, specialist requirements, and high-volume workforce needs. Search, screening, assessment, offer management, and post-placement support.",
      areaServed: "Worldwide",
    },
  },
  recruitmentServices: {
    title: "Services — 25th & Staffing",
    description:
      "Flexible recruitment support across individual roles, specialist searches, and full recruitment process delivery. Adapts to your hiring volume, timelines, and structure.",
    schema: {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: "25th & Staffing Recruitment Services",
      provider: {
        "@type": "Organization",
        name: "25th & Staffing",
      },
      serviceType: "Recruitment Services",
      description:
        "Flexible recruitment support across individual roles, specialist searches, recurring hiring programmes, and full recruitment process delivery.",
      areaServed: "Worldwide",
    },
  },
  direct25th: {
    title: "25th Direct — AI Candidate Sourcing and Screening",
    description:
      "Pull candidates from multiple talent sources, parse CVs against your requirements, and receive a structured shortlist before interview. Faster search, less manual work.",
    schema: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "25th Direct",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      provider: {
        "@type": "Organization",
        name: "25th & Staffing",
      },
      description:
        "Candidate sourcing and screening platform. Pulls from multiple talent sources, parses CVs against role requirements, and delivers a structured shortlist before interview.",
      offers: {
        "@type": "Offer",
        description: "Contact 25th & Staffing for pricing.",
      },
    },
  },
  aiRecruiter: {
    title: "AI Recruiter — Structured AI Interviews at Scale",
    description:
      "AI-powered structured interviews, behavioural tracking, and candidate reports at scale. Replace first-stage interviews with a consistent, automated assessment process.",
    schema: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "AI Recruiter by 25th & Staffing",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      provider: {
        "@type": "Organization",
        name: "25th & Staffing",
      },
      description:
        "Structured AI interview and candidate assessment platform. Conducts AI-powered interviews, evaluates responses, tracks behavioural signals, and delivers candidate reports at scale.",
      featureList: [
        "Structured AI interviews",
        "Behavioural signal tracking",
        "Automated scheduling",
        "Candidate assessment reports",
      ],
      offers: {
        "@type": "Offer",
        description: "Contact 25th & Staffing for pricing.",
      },
    },
  },
  coreBOS: {
    title: "Core — Business Operating System for African SMEs",
    description:
      "Centralise payroll, HR, compliance, and financial management in one platform. Core SME for registered businesses. Core Retail for merchants. Built on eleven years of workforce intelligence.",
    schema: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Core by 25th & Staffing",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web, Android",
      provider: {
        "@type": "Organization",
        name: "25th & Staffing",
      },
      description:
        "Business operating system for SMEs and retailers across Africa and global markets. Centralises payroll, HR, compliance, and financial management in one platform.",
      featureList: [
        "Payroll management",
        "HR framework",
        "Compliance calendar",
        "Financial reporting",
        "NAV tracking",
        "Leave management",
        "Staff records",
        "Inventory management (Core Retail)",
        "Sales tracking (Core Retail)",
      ],
      offers: [
        {
          "@type": "Offer",
          name: "Core SME",
          description:
            "For registered businesses with staff, payroll, and compliance obligations.",
        },
        {
          "@type": "Offer",
          name: "Core Retail",
          description:
            "For merchants managing inventory, staff, and sales from an Android device.",
        },
      ],
    },
  },
  suite25th: {
    title: "25th Suite — Intelligent Workforce for Enterprise",
    description:
      "Intelligent workforce solution for organisations with 1,000 or more employees. Managed payroll, HR, compliance, and workforce intelligence. Onboarding by invitation.",
    schema: {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: "25th Suite",
      provider: {
        "@type": "Organization",
        name: "25th & Staffing",
      },
      serviceType: "Enterprise Workforce Management",
      description:
        "Intelligent workforce solution for enterprise organisations with 1,000 or more employees. Integrates payroll, HR operations, compliance management, and workforce intelligence into a bespoke managed engagement.",
      areaServed: "Worldwide",
      offers: {
        "@type": "Offer",
        name: "25th Suite",
        description:
          "Bespoke enterprise engagement. Onboarding by invitation following a senior leadership conversation with 25th & Staffing.",
      },
    },
  },
  // Additional pages SEO configuration
  contact: {
    title: "Contact Us — 25th & Staffing",
    description:
      "Get in touch with 25th & Staffing for executive search, permanent staffing, or workforce technology consultation. Global reach, African roots.",
  },
  marketplace: {
    title: "Job Marketplace & Career Opportunities — 25th & Staffing",
    description:
      "Explore high-impact executive and specialist career opportunities across top global companies with 25th & Staffing.",
  },
  teams: {
    title: "Leadership & Team — 25th & Staffing",
    description:
      "Meet the experienced recruitment professionals, executive search advisors, and HR technology innovators behind 25th & Staffing.",
  },
  origin: {
    title: "Our Story & Origin — 25th & Staffing",
    description:
      "Discover the journey of 25th & Staffing over eleven years: connecting African leadership talent with world-class global organizations.",
  },
  faq: {
    title: "Frequently Asked Questions — 25th & Staffing",
    description:
      "Find answers to common questions regarding our retained executive search process, permanent staffing solutions, and HR technology platforms.",
  },
  terms: {
    title: "Terms & Conditions — 25th & Staffing",
    description:
      "Review the terms and conditions governing the use of 25th & Staffing services, workforce solutions, and digital platforms.",
  },
  policy: {
    title: "Privacy Policy — 25th & Staffing",
    description:
      "Learn how 25th & Staffing collects, protects, and handles personal data and candidate privacy in compliance with data regulations.",
  },
  jobSeeker: {
    title: "Talent Network & Job Seekers — 25th & Staffing",
    description:
      "Join the 25th & Staffing talent network. Be visible to global companies and receive AI-matched career opportunities.",
  },
  packages: {
    title: "Recruitment Packages & Offerings — 25th & Staffing",
    description:
      "Explore flexible recruitment packages tailored for entry-level, executive search, and enterprise workforce management.",
  },
};
